import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRoleChange = (value) => {
    setFormData({ ...formData, role: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.role) {
      toast.error('Please select your account type');
      return;
    }

    setIsLoading(true);

    const result = await register(formData);

    if (result.success) {
      setRegistrationComplete(true);
    } else {
      toast.error(result.error);
    }

    setIsLoading(false);
  };

  // Show success message after registration
  if (registrationComplete) {
    return (
      <div className="min-h-screen flex">
        {/* Left side - Success Message */}
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-800">
          <div className="max-w-md w-full">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-heading uppercase text-pfap-green dark:text-gray-100 mb-2" >
                    Registration Submitted!
                  </h2>
                  <p className="text-gray-600 dark:text-gray-500 mb-4">
                    Your account has been created and is pending approval.
                  </p>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4 border border-emerald-200">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>What happens next?</strong>
                    </p>
                    <ul className="text-sm text-gray-600 dark:text-gray-500 mt-2 text-left list-disc list-inside space-y-1">
                      <li>An academy administrator will review your registration</li>
                      <li>Once approved, you will be linked to your athlete profile</li>
                      <li>You will then be able to log in and access your data</li>
                    </ul>
                  </div>
                  <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded-lg">
                    Please wait for admin approval before attempting to log in.
                  </p>
                  <Link to="/login">
                    <Button variant="outline" className="mt-4">
                      Return to Login
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Right side - Branding */}
        <div className="hidden lg:flex flex-1 bg-gradient-to-br bg-pfap-green items-center justify-center p-12">
          <div className="max-w-md text-white text-center">
            <img src="/pfap-full-white.svg" alt="PFAP" className="h-24 mx-auto mb-8" />
            <h1 className="text-4xl font-heading uppercase text-white mb-4" >
              PFAP Hub
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              Pro-Football Academy Portugal
            </p>
            <p className="mt-3 text-slate-400 text-sm">
              Complete Academy Management Platform
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-800">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center lg:text-left">
            <img src="/pfap-logo-black.png" alt="PFAP" className="h-16 mx-auto lg:mx-0 mb-4" />
            <h2 className="text-3xl sm:text-4xl font-heading uppercase text-pfap-green dark:text-gray-100" >
              Create Account
            </h2>
            <p className="mt-2 text-base text-gray-600 dark:text-gray-500">
              Register as a Player or Parent to access PFAP Hub
            </p>
          </div>
          
          <form className="mt-8 space-y-6" onSubmit={handleSubmit} data-testid="register-form">
            <div className="space-y-4">
              <div>
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={handleChange}
                  className="mt-1"
                  placeholder="John Doe"
                  data-testid="register-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1"
                  placeholder="you@example.com"
                  data-testid="register-email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="mt-1"
                  placeholder="••••••••"
                  data-testid="register-password-input"
                />
              </div>

              <div>
                <Label htmlFor="role">I am a...</Label>
                <Select onValueChange={handleRoleChange} required>
                  <SelectTrigger className="mt-1" data-testid="register-role-select">
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Player" data-testid="role-player">Player</SelectItem>
                    <SelectItem value="Parent" data-testid="role-parent">Parent / Guardian</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  Staff and Admin accounts are created by administrators only.
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Your account will need to be approved by an administrator before you can log in. 
                You will be linked to your athlete profile during the approval process.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-pfap-green hover:bg-pfap-green/90 text-white"
              disabled={isLoading}
              data-testid="register-submit-button"
            >
              {isLoading ? 'Creating account...' : 'Create account'}
            </Button>

            <p className="text-center text-sm text-gray-600 dark:text-gray-500">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-pfap-blue hover:text-pfap-blue/80">
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </div>

      {/* Right side - Branding */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br bg-pfap-green items-center justify-center p-12">
        <div className="max-w-md text-white text-center">
          <img src="/pfap-full-white.svg" alt="PFAP" className="h-24 mx-auto mb-8" />
          <h1 className="text-4xl font-heading uppercase text-white mb-4" >
            PFAP Hub
          </h1>
          <p className="text-lg text-slate-300 leading-relaxed">
            Pro-Football Academy Portugal
          </p>
          <p className="mt-3 text-slate-400 text-sm">
            Complete Academy Management Platform
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
